/* eslint-disable curly */
/*
Author: Nishant Singh
*/

import { Cart } from '~api/cart';
import { Catalog } from '~api/catalog';

import AsyncStorage from '~global/storage';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAttributesName } from '~global/helpers';
import { GoogleTagManager } from '~global/sociolla.dm.lib';
import { isNative } from '~global/screens';
// ...........Actions creator
export const CART_PROUDUCT_COUNT = 'CART_PROUDUCT_COUNT';
export const CART_STORES_LIST = 'CART_STORES_LIST';

export const CART_DETAIL = 'CART_DETAIL';
export const CART_ADD = 'CART_ADD';
export const CART_REORDER = 'CART_REORDER';
export const CART_RESET = 'CART_RESET';
export const CART_RESET_VOUCHER = 'CART_RESET_VOUCHER';

const STORAGE_KEYS = {
  PENDING_CART: 'pending_cart_items',
  CART: 'cart',
};

let pendingCartSyncPromise = null;
let pendingCartProcessedForSession = false;

// Normalize product/combination IDs for comparison: treats null, undefined, '' as equivalent
const normalizeId = (id) => (id != null && id !== '' ? String(id) : '');

// ...........Device storage
// showAsyncStorageContentInDev()
const saveDataToStorage = async (key, data) => {
  return await AsyncStorage.setItem(key, JSON.stringify(data));
};

const getDataFromStorage = async (key) => {
  return await AsyncStorage.getItem(key);
};

const removeDataFromStorage = async (key) => {
  return await AsyncStorage.removeItem(key);
};

export const addProductToCart = createAsyncThunk(
  CART_ADD,
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const isLoggedIn = state.auth.isLoggedIn;

    if (!isLoggedIn) {
      if (payload.action === 'delete') {
        try {
          const pendingCart = await getDataFromStorage(
            STORAGE_KEYS.PENDING_CART,
          );
          const cartItems = pendingCart ? JSON.parse(pendingCart) : [];

          const filteredItems = cartItems.filter(
            (item) =>
              normalizeId(item.product.id) !==
                normalizeId(payload.product.id) ||
              normalizeId(item.product.combination_id) !==
                normalizeId(payload.product.combination_id),
          );

          await saveDataToStorage(STORAGE_KEYS.PENDING_CART, filteredItems);

          return {
            success: true,
            pending: true,
            message: 'Item removed from pending cart',
          };
        } catch (err) {
          console.error('Remove from pending cart error:', err);
          return thunkAPI.rejectWithValue({
            message: 'Failed to remove item from pending cart',
          });
        }
      } else if (payload.action === 'plus' || payload.action === 'minus') {
        try {
          const pendingCart = await getDataFromStorage(
            STORAGE_KEYS.PENDING_CART,
          );
          const cartItems = pendingCart ? JSON.parse(pendingCart) : [];

          const existingItemIndex = cartItems.findIndex(
            (item) =>
              normalizeId(item.product.id) ===
                normalizeId(payload.product.id) &&
              normalizeId(item.product.combination_id) ===
                normalizeId(payload.product.combination_id),
          );

          if (existingItemIndex !== -1) {
            const currentQuantity =
              cartItems[existingItemIndex].product.quantity || 1;
            let newQuantity = currentQuantity;

            if (payload.action === 'plus') {
              newQuantity = currentQuantity + (payload.product.quantity || 1);
            } else if (payload.action === 'minus') {
              newQuantity = Math.max(
                1,
                currentQuantity - (payload.product.quantity || 1),
              );
            } else if (payload.product.quantity) {
              newQuantity = payload.product.quantity;
            }

            // Update quantity in cart item
            cartItems[existingItemIndex].product.quantity = newQuantity;

            if (cartItems[existingItemIndex].product.detailData) {
              cartItems[existingItemIndex].product.detailData.quantity =
                newQuantity;
            }

            await saveDataToStorage(STORAGE_KEYS.PENDING_CART, cartItems);

            return {
              success: true,
              pending: true,
              message: 'Item quantity updated in cart',
              quantity: newQuantity,
            };
          } else {
            await thunkAPI.dispatch(storePendingCartBag(payload));
            return {
              success: true,
              pending: true,
              message: 'Item stored in pending cart',
            };
          }
        } catch (err) {
          console.error('Update cart quantity error:', err);
          return thunkAPI.rejectWithValue({
            message: 'Failed to update item quantity in cart',
          });
        }
      }

      await thunkAPI.dispatch(storePendingCartBag(payload));
      thunkAPI.dispatch(getUserCart());
      thunkAPI.dispatch(getCartProductCount());

      return {
        success: true,
        pending: true,
        message: 'Item stored in pending cart',
      };
    }

    try {
      if (isLoggedIn && payload.product?.detailData) {
        delete payload.product.detailData;
      }

      const res = await Cart.addProductToCartRequest(payload);

      return res.data;
    } catch (err) {
      console.error('Add to cart error:', err.message);
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const cartReorder = createAsyncThunk(
  CART_REORDER,
  async (payload, thunkAPI) => {
    try {
      const res = await Cart.cartReorderRequest(payload);
      if (res.status === 200) {
        thunkAPI.dispatch(getCartProductCount());
      }
    } catch (err) {
      console.error(err.message);
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const addProductToLocalStorage = (product) => {
  return async () => {
    try {
      const payload = {
        brand: product.brand.name,
        combination_name: getAttributesName(
          product.default_combination.attributes,
        ),
        combination_id: product.default_combination._id,
        price: product.minimun_price_after_discount,
        qty: 1,
        _id: product._id,
        id: product.id,
        raw: product,
      };
      let cart = await getDataFromStorage('cart');
      if (!cart) {
        await saveDataToStorage('cart', [payload]);
      } else {
        cart = JSON.parse(cart);
        cart.push(payload);
      }
    } catch (err) {
      console.error(err.message);
    }
  };
};

export const getUserCart = createAsyncThunk(
  CART_DETAIL,
  async (params, thunkAPI) => {
    const isLoggedIn = thunkAPI.getState().auth.isLoggedIn;
    if (!isLoggedIn) {
      try {
        const pendingCart = await getDataFromStorage(STORAGE_KEYS.PENDING_CART);
        const cartItems = pendingCart ? JSON.parse(pendingCart) : [];

        if (cartItems.length > 0) {
          const products = cartItems.map((item) => item.product.detailData);

          if (products) {
            products.forEach((product, idx) => {
              if (
                cartItems[idx].product.combination_id &&
                product?.combinations?.length > 0
              ) {
                const matchingCombination = product.combinations.find(
                  (comb) => comb._id === cartItems[idx].product.combination_id,
                );
                if (matchingCombination) {
                  product.detail.combination = matchingCombination;
                  product.detail.image = matchingCombination.images[0];
                }
              }

              if (
                !product?.quantity ||
                isNaN(product.quantity) ||
                product.quantity <= 0
              ) {
                product.quantity = 1;
              }
            });
            const totalAmount = products.reduce((sum, product) => {
              const priceString = product.price_range || '0';
              const numericPrice = parseInt(
                priceString.replace('Rp', '').replace(/\./g, ''),
                10,
              );
              return sum + (numericPrice || 0) * product.quantity;
            }, 0);

            const totalAmountAfterDiscount = products.reduce((sum, product) => {
              const numericPrice =
                product?.default_combination?.price_after_discount ||
                product?.max_price_after_discount ||
                '0';

              return sum + (numericPrice || 0) * product.quantity;
            }, 0);

            const cartData = {
              products: products,
              products_count: products.length,
              total_amount_after_discount: totalAmountAfterDiscount,
              total_amout: totalAmount,
              total_amount_without_extra: totalAmountAfterDiscount,
              unselected_products: [],
              free_products: [],
              extra_bag_rules: [],
              shipping_fee_rules: 0,
              shipping_fee_type: 'regular',
            };

            if (!isNative) {
              GoogleTagManager.trackCartInsider(cartData);
            }

            return cartData;
          }
        }

        return {
          items: [],
          total: 0,
          count: 0,
        };
      } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data);
      }
    }

    try {
      await thunkAPI.dispatch(processPendingCart());

      const res = await Cart.userCartRequest(params);
      if (res.status === 200) {
        const cartData = res.data.data;

        if (!isNative && isLoggedIn && cartData) {
          GoogleTagManager.trackCartInsider(cartData);
        }

        return cartData;
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || { error: true });
    }
  },
);

export const getCartProductCount = createAsyncThunk(
  CART_PROUDUCT_COUNT,
  async (_, thunkAPI) => {
    const isLoggedIn = thunkAPI.getState().auth.isLoggedIn;

    if (!isLoggedIn) {
      const pendingCart = await getDataFromStorage(STORAGE_KEYS.PENDING_CART);
      const cartItems = pendingCart ? JSON.parse(pendingCart) : [];

      let totalCount = 0;

      cartItems.forEach((item) => {
        const quantity = item.product.quantity || 1;
        totalCount += quantity;
      });

      return totalCount;
    }

    try {
      const res = await Cart.cartProductCountRequest();
      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || { error: true });
    }
  },
);

export const setProductCount = createAsyncThunk(
  CART_PROUDUCT_COUNT,
  async (count) => {
    return count;
  },
);

export const getSpecialPriceProducts = () => {
  return async (dispatch, getState) => {
    const isLoggedIn = getState().auth.isLoggedIn;

    if (!isLoggedIn) {
      return [];
    }

    try {
      const res = await Cart.specialPriceProductsRequest();
      if (res.status === 200) {
        return res.data.data;
      }

      return [];
    } catch (err) {
      console.error(err.message);
      return [];
    }
  };
};

export const getGwpProducts = (params) => {
  return async (dispatch, getState) => {
    const isLoggedIn = getState().auth.isLoggedIn;

    if (!isLoggedIn) {
      return [];
    }

    try {
      const res = await Cart.gwpProductsRequest(params);
      if (res.status === 200) {
        return res.data.data;
      }
      return [];
    } catch (err) {
      console.error(err.message);
      return [];
    }
  };
};

export const getPoints = (params) => {
  return async () => {
    try {
      const res = await Cart.pointsRequest(params);
      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      console.error(err.message);
    }
  };
};

export const applyRedeemPoints = (payload) => {
  return async () => {
    try {
      const res = await Cart.applyRedeemPointsRequest(payload);
      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      return err.response.data.data;
    }
  };
};

export const cancelRedeemPoints = (params) => {
  return async () => {
    try {
      const res = await Cart.cancelRedeemPointsRequest(params);
      if (res.status === 200) {
        return res.data.success;
      }
    } catch (err) {
      console.error(err.message);
    }
  };
};

export const getExtrabags = createAsyncThunk(
  'cart/extrabags',
  async () =>
    await Cart.getExtrabagsRequest({
      filter: { is_active_in_lulla: true },
      sort: '-created_at',
      limit: 3,
    }).then(({ data: { data } }) => data),
);

export const toggleExtrabag = createAsyncThunk(
  'cart/toggleExtrabag',
  async ({ selected, id }, { dispatch }) =>
    await Cart.toggleExtrabagRequest({
      action: selected ? 'apply' : 'remove',
      id,
    })
      .then(() => dispatch(getExtrabags()))
      .then(() => dispatch(getUserCart())),
);

export const changeAddress = createAsyncThunk(
  'cart/changeAddress',
  async ({ cartId, addressId }, { dispatch }) =>
    await Cart.updateCartAddress(cartId, addressId).then(() =>
      dispatch(getUserCart()),
    ),
);

export const selectProductCart = createAsyncThunk(
  'cart/selectProduct',
  async (payload, thunkAPI) => {
    const UNSELECTED_PRODUCTS_KEY = 'unselected_products_cache';
    const cartDetail = thunkAPI.getState().cart.cartDetail;
    const isLoggedIn = thunkAPI.getState().auth.isLoggedIn;

    try {
      let productInCache = false;
      let cachedData = {};
      let productCacheData = {};
      const cartId = cartDetail?._id || 'default_cart';
      if (!isLoggedIn) {
        try {
          const cachedDataString = await AsyncStorage.getItem(
            UNSELECTED_PRODUCTS_KEY,
          );
          if (cachedDataString) {
            cachedData = JSON.parse(cachedDataString);
            if (cachedData[cartId]) {
              productInCache = cachedData[cartId].some((product) =>
                payload.products.some((p) => p.product_id === product.id),
              );
              productCacheData = cachedData[cartId].find((product) =>
                payload.products.some((p) => p.product_id === product.id),
              );
            }
          }
        } catch (storageError) {
          console.error(
            'Error checking unselected products cache:',
            storageError,
          );
        }
      }

      let res;
      if (productInCache && !isLoggedIn) {
        res = await Cart.addProductToCartRequest({
          product: {
            id: productCacheData.id,
            combination_id: productCacheData.combination_id,
            quantity: productCacheData?.quantity ?? 1,
          },
          action: 'add',
        });
      } else {
        res = await Cart.selectProductCartRequest(payload);
      }

      if (res.status === 200) {
        if (!isLoggedIn) {
          try {
            const cachedDataString = await AsyncStorage.getItem(
              UNSELECTED_PRODUCTS_KEY,
            );
            if (cachedDataString) {
              const cachedData = JSON.parse(cachedDataString);
              if (cachedData[cartId]) {
                const updatedUnselectedProducts = cachedData[cartId].filter(
                  (product) => product._id !== payload.product_id,
                );

                if (updatedUnselectedProducts.length === 0) {
                  delete cachedData[cartId];
                } else {
                  cachedData[cartId] = updatedUnselectedProducts;
                }

                if (Object.keys(cachedData).length === 0) {
                  await AsyncStorage.removeItem(UNSELECTED_PRODUCTS_KEY);
                } else {
                  await AsyncStorage.setItem(
                    UNSELECTED_PRODUCTS_KEY,
                    JSON.stringify(cachedData),
                  );
                }
              }
            }
          } catch (storageError) {
            console.error(
              'Error updating unselected products cache:',
              storageError,
            );
          }
        }

        return res.data.data;
      }

      return res.data;
    } catch (err) {
      console.error('Select product cart error:', err);
      return thunkAPI.rejectWithValue(err?.response?.data || { error: true });
    }
  },
);

export const deleteProductCart = (params) => {
  return async (dispatch, getState) => {
    try {
      const { auth } = getState();
      const isLoggedIn = auth?.isLoggedIn || false;

      if (!isLoggedIn) {
        const pendingCartString = await getDataFromStorage(
          STORAGE_KEYS.PENDING_CART,
        );

        const pendingCartItems = pendingCartString
          ? JSON.parse(pendingCartString)
          : [];

        const updatedItems = pendingCartItems.filter(
          (item) => item.id !== params.id && item.product?.id !== params.id,
        );

        await saveDataToStorage(STORAGE_KEYS.PENDING_CART, updatedItems);

        dispatch(getCartProductCount());

        return {
          status: 200,
          data: {
            message: 'Item removed from cart successfully',
          },
        };
      }

      const res = await Cart.deleteProductCartRequest(params);

      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      console.error('Error deleting cart item:', err);
      return (
        err?.response?.data?.data || {
          error: true,
          message: 'Failed to delete item from cart',
        }
      );
    }
  };
};

export const addWislistsCart = (payload) => {
  return async () => {
    try {
      const res = await Catalog.addWishlistsCart(payload);
      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      return err.response.data.data;
    }
  };
};

export const getPointsLimit = () => {
  return async () => {
    try {
      const res = await Cart.getPointsLimit();
      if (res.status === 200) {
        return res.data.data;
      }
    } catch (err) {
      console.error(err.message);
    }
  };
};

export const storePendingCartBag = (payload) => {
  return async () => {
    try {
      const cart = await getDataFromStorage(STORAGE_KEYS.PENDING_CART);
      let cartItems = cart ? JSON.parse(cart) : [];

      const isPayloadPreOrder =
        payload.product?.detailData?.detail?.combination?.is_pre_order;
      const payloadDeliveryDate =
        payload.product?.detailData?.detail?.combination?.preorder?.find(
          (item) => item.delivery_date,
        );

      if (isPayloadPreOrder) {
        const existingPreOrders = cartItems.filter(
          (item) => item.product?.detailData?.detail?.combination?.is_pre_order,
        );

        if (existingPreOrders.length > 0) {
          const existingDeliveryDate =
            existingPreOrders[0].product?.detailData?.detail?.combination?.preorder?.find(
              (item) => item.delivery_date,
            );

          if (existingDeliveryDate !== payloadDeliveryDate) {
            cartItems = [];
          }
        } else {
          cartItems = [];
        }
      } else {
        const hasPreOrderItems = cartItems.some(
          (item) => item.product?.detailData?.detail?.combination?.is_pre_order,
        );

        if (hasPreOrderItems) {
          cartItems = [];
        }
      }

      const existingItemIndex = cartItems.findIndex(
        (item) =>
          normalizeId(item.product.id) === normalizeId(payload.product.id) &&
          normalizeId(item.product.combination_id) ===
            normalizeId(payload.product.combination_id),
      );

      if (existingItemIndex !== -1) {
        const currentQuantity =
          cartItems[existingItemIndex].product.quantity || 1;
        let newQuantity = payload.product.quantity || 1;

        if (payload.action === 'plus') {
          newQuantity = currentQuantity + newQuantity;
        } else if (payload.action === 'minus') {
          newQuantity = Math.max(1, currentQuantity - newQuantity);
        } else if (payload.action === 'set' || payload.action === 'replace') {
          newQuantity = payload.product.quantity;
        } else {
          newQuantity = currentQuantity + newQuantity;
        }

        cartItems[existingItemIndex].product.quantity = newQuantity;

        if (cartItems[existingItemIndex].product.detailData) {
          cartItems[existingItemIndex].product.detailData.quantity =
            newQuantity;
        }
      } else {
        if (!payload.product.quantity) {
          payload.product.quantity = 1;
        }

        cartItems.unshift(payload);
      }
      await saveDataToStorage(STORAGE_KEYS.PENDING_CART, cartItems);
    } catch (err) {
      console.error('Error storing pending cart:', err.message);
    }
  };
};

export const resetPendingCartFlag = () => {
  pendingCartProcessedForSession = false;
  pendingCartSyncPromise = null;
};

export const processPendingCart = () => {
  return async (dispatch, getState) => {
    const isLoggedIn = getState().auth.isLoggedIn;

    if (!isLoggedIn || pendingCartProcessedForSession) {
      return;
    }

    if (pendingCartSyncPromise) {
      await pendingCartSyncPromise;
      return;
    }

    const syncPromise = syncPendingCartItems(dispatch);
    pendingCartSyncPromise = syncPromise;
    try {
      await syncPromise;
    } finally {
      if (pendingCartSyncPromise === syncPromise) {
        pendingCartSyncPromise = null;
      }
    }
  };
};

const syncPendingCartItems = async (dispatch) => {
  try {
    const pendingItems = await dispatch(getPendingCartItems());

    if (pendingItems?.length > 0) {
      dispatch({ type: 'MODAL/SHOW', modalName: 'loading' });

      for (let i = 0; i < pendingItems.length; i++) {
        const item = pendingItems[i];
        try {
          const res = await Cart.addProductToCartRequest(item);
          if (res.status === 200) {
            await dispatch(getCartProductCount());
          }
        } catch (error) {
          console.error('Error processing item:', error);
        }
      }

      await dispatch(clearPendingCartItems());

      dispatch({ type: 'MODAL/HIDE', modalName: 'loading' });
      dispatch({ type: 'shoppingBag/setIsReloadCart', data: true });

      pendingCartProcessedForSession = true;
    }
  } catch (err) {
    console.error('Error processing pending cart:', err);
    dispatch({ type: 'MODAL/HIDE', modalName: 'loading' });
  }
};

export const getPendingCartItems = () => {
  return async () => {
    try {
      const cart = await getDataFromStorage(STORAGE_KEYS.PENDING_CART);
      return cart ? JSON.parse(cart) : [];
    } catch (err) {
      console.error('Error getting pending cart:', err.message);
      return [];
    }
  };
};

export const clearPendingCartItems = () => {
  return async () => {
    try {
      await removeDataFromStorage(STORAGE_KEYS.PENDING_CART);
    } catch (err) {
      console.error('Error clearing pending cart:', err.message);
    }
  };
};

export const getPickupStoreLocation = createAsyncThunk(
  CART_STORES_LIST,
  async (params, thunkAPI) => {
    const isLoggedIn = thunkAPI.getState().auth.isLoggedIn;

    if (!isLoggedIn) {
      return [];
    }

    try {
      const res = await Cart.getPickupStoreLocation(params);
      if (res.status === 200) {
        return res.data?.data;
      }

      return [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.message || { error: true });
    }
  },
);
